"use client"

import FormError from "@/components/FormError"
import Price from "@/components/Price"
import RichContent from "@/components/RichContent/richContent"
import { Form } from "@/components/ui/form"
import { cn } from "@/lib/utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useIntl } from "react-intl"
import { mittInstance } from "@/constants/mittInstance"
import { SIZE_CHART_SKU } from "@/constants/size-chart-sku"
import { formatString } from "@/lib/format-string"
import {
  ConfigurableProduct,
  ConfigurableProductOptions,
  Maybe,
} from "@/types/graphql.prod"
import { isSupportedProductType as isSupported } from "@/util/isSupportedProductType"
import { ratingToStarValue } from "@/util/rating-to-star-value"
import Link from "next/link"
import { useMemo, useState } from "react"
import { z } from "zod"

import { NewNotifyMeFormDialog } from "./NotifyMeFormDialog/new-notify-me-form-dialog"
import { calculateDeliveryDate, getIsPreOrderInfo } from "./productFullDetail"
import { QuantitySelect } from "./quantity-select"
import { StandardOptions } from "./standard-options"
import { useAddToCart } from "./use-add-to-cart"
import { useGetIsOutOfStock } from "./use-get-is-out-of-stock"
import { useGetOptionCode } from "./use-get-option-code"
import { useGetProductPrice } from "./use-get-product-price"
import { useOptionsSpecs } from "./use-options-sepcs"
import { useGetMediaGalleryEntries } from "./useGetMediaGalleryEntries"
import ProductImageCarousel from "@/components/product-image-carousel"
import Rate from "@/components/Rate"
import { AddCartButton } from "./add-cart-button"
import { ProductDetailAndShipping } from "./product-detail-and-shipping"
import classes from "./productFullDetail.module.css"
import { useIsMobile } from "@/hooks/use-is-mobile"
import { ProductCarousel } from "./product-carousel"
import { useRouter } from "next/navigation"
import { setPosition } from "@/util/position-storage"

export interface SelectedConfigurableOption {
  uid: string
  label: string
  option_attribute_id: string
  option_selection_value: number
}

export interface SelectedOptions {
  [key: string]: SelectedConfigurableOption | number | string
  quantity: number | string
}

export interface SelectedOptionsWithoutQuantity {
  [key: string]: SelectedConfigurableOption
}

// Correlate a GQL error message to a field. GQL could return a longer error
// string but it may contain contextual info such as product id. We can use
// parts of the string to check for which field to apply the error.
const ERROR_MESSAGE_TO_FIELD_MAPPING: { [key: string]: string } = {
  "The requested qty is not available": "quantity",
  "Product that you are trying to add is not available.": "quantity",
  "The product that was requested doesn't exist.": "quantity",
}

// Field level error messages for rendering.
const ERROR_FIELD_TO_MESSAGE_MAPPING: { [key: string]: string } = {
  quantity: "The requested quantity is not available.",
}

export const ProductFullDetailQuickView = ({
  product,
  defaultValues,
  handleColorLabelChange,
  seoColorLabel,
  setOpen,
  setQuickViewSuccessOpen,
  setQuickViewSuccessNumber,
  afterSubmit,
}: {
  product: ConfigurableProduct
  defaultValues: any
  handleColorLabelChange: any
  seoColorLabel: string
  setOpen: any
  setQuickViewSuccessOpen: any
  setQuickViewSuccessNumber: any
  afterSubmit?: any
}) => {
  console.log({
    product,
  })

  const { isMobile } = useIsMobile()
  const router = useRouter()
  const createFormSchema = (
    options: Maybe<Maybe<ConfigurableProductOptions>[]> | undefined
  ) => {
    const schemaFields: Record<string, z.ZodTypeAny> = {}

    schemaFields["quantity"] = z.string().min(1, {
      message: `This is a required field!`,
    })

    if (!options) {
      return z.object(schemaFields)
    }

    options?.forEach((item) => {
      if (item?.attribute_code) {
        schemaFields[item?.attribute_code] = z.object(
          {
            uid: z.string().min(1),
            label: z.string(),
            option_attribute_id: z.string(),
            option_selection_value: z.number().optional(),
          },
          {
            message: `This is a required field!`,
          }
        )
      }
    })

    return z.object(schemaFields)
  }

  const FormSchema = createFormSchema(product?.configurable_options)

  const form = useForm({
    resolver: zodResolver(FormSchema as any),
    defaultValues: defaultValues,
    shouldFocusError: true,
  })
  const allCPData = form.watch() as SelectedOptions
  const allCpDataWithoutQuantity = Object.fromEntries(
    Object.entries(allCPData).filter(([key]) => key !== "quantity")
  ) as SelectedOptionsWithoutQuantity
  const selectedOptionsArray = useMemo(() => {
    return Object.values(allCpDataWithoutQuantity)
      .filter((item) => item && item.uid)
      .map((item) => item.uid)
  }, [allCpDataWithoutQuantity])
  const { optionCodes } = useGetOptionCode({
    product,
  })
  const optionSelections: Map<any, any> = useMemo(() => {
    const optionMap = new Map()
    const options = product?.configurable_options || []

    options?.forEach((item) => {
      const attribute_id = item?.attribute_id

      optionMap.set(attribute_id, undefined)
    })

    // Iterate through each property in the JSON object
    for (const [key, value] of Object.entries(allCpDataWithoutQuantity)) {
      if (typeof value === "object" && value !== null) {
        const item = allCpDataWithoutQuantity[key]

        optionMap.set(item.option_attribute_id, item.option_selection_value)
      }
    }

    return optionMap
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCpDataWithoutQuantity])

  const isPreOrderInfo = useMemo(
    () => getIsPreOrderInfo(product, optionCodes, optionSelections),
    [product, optionCodes, optionSelections]
  )

  const productType = product?.__typename
  const isSupportedProductType = isSupported(productType)
  const [notifyMeFormDialogIsOpen, setNotifyMeFormDialogIsOpen] =
    useState(false)
  const { productPrice } = useGetProductPrice({
    product,
    optionCodes,
    optionSelections,
  })
  const { mediaGalleryEntries } = useGetMediaGalleryEntries({
    product,
    optionCodes,
    optionSelections,
  })

  const { isOutOfStock } = useGetIsOutOfStock({
    product,
    optionCodes,
    optionSelections,
  })
  const productDetails = {
    description: product?.description,
    shortDescription: product?.short_description,
    name: product?.name,
    price: productPrice?.final_price,
    sku: product?.sku,
    videoUrl: product?.video_url,
  }
  const { handleAddToCart, isAddingProduct, addToCartStatus, errorMessage } =
    useAddToCart({
      product,
      mediaGalleryEntries,
      selectedOptionsArray,
      afterSubmit: afterSubmit,
    })

  const { isPreOrder, note, label } = isPreOrderInfo || {}

  const { formatMessage } = useIntl()

  // eslint-disable-next-line no-undef
  const errors = new Map()

  if (errorMessage) {
    Object.keys(ERROR_MESSAGE_TO_FIELD_MAPPING).forEach((key) => {
      if (errorMessage.includes(key)) {
        const target = ERROR_MESSAGE_TO_FIELD_MAPPING[key]
        const message = ERROR_FIELD_TO_MESSAGE_MAPPING[target as any]

        errors.set(target, message)
      }
    })

    // Handle cases where a user token is invalid or expired. Preferably
    // this would be handled elsewhere with an error code and not a string.
    if (errorMessage.includes("The current user cannot")) {
      errors.set("form", [
        new Error(
          formatMessage({
            id: "productFullDetail.errorToken",
            defaultMessage:
              "There was a problem with your cart. Please sign in again and try adding the item once more.",
          })
        ),
      ])
    }

    // Handle cases where a cart wasn't created properly.
    if (errorMessage.includes('Variable "$cartId" got invalid value null')) {
      errors.set("form", [
        new Error(
          formatMessage({
            id: "productFullDetail.errorCart",
            defaultMessage:
              "There was a problem with your cart. Please refresh the page and try adding the item once more.",
          })
        ),
      ])
    }

    // An unknown error should still present a readable message.
    if (!errors.size) {
      errors.set("form", [
        new Error(
          formatMessage({
            id: "productFullDetail.errorUnknown",
            defaultMessage:
              "Could not add item to cart. Please check required options and try again.",
          })
        ),
      ])
    }
  }

  const { optionSpecs, handleClick } = useOptionsSpecs({
    product,
  })

  const rating = useMemo(() => {
    return product && product.review_count ? (
      <section className={"inline-block flex items-center"}>
        <Rate
          disabled
          initialRating={ratingToStarValue(product?.rating_summary)}
        />{" "}
        <span
          onClick={() => {
            setPosition("review")
            router.push(`/${product.url_key}.html`)
          }}
          className="ml-[10px] cursor-pointer font-sans text-sm text-title underline hover_text-primary"
        >
          {product.review_count} Reviews
        </span>
      </section>
    ) : (
      <section className={"inline-block flex items-center"}>
        <Rate disabled initialRating={0} />
        <span className="ml-[10px] cursor-pointer font-sans text-sm text-title underline hover_text-primary">
          Be the first to review
        </span>
      </section>
    )
  }, [product])

  const questions = product.question_answer?.length ? (
    <span className="flex items-center">
      <span className="ml-[10px] inline-block h-[20px] w-[1px] bg-[#000]"></span>
      <span
        onClick={() => {
          setPosition("question")
          router.push(`/${product.url_key}.html`)
        }}
        className="ml-[10px] cursor-pointer font-sans text-sm text-title underline hover_text-primary"
      >
        {product.question_answer?.length} Questions
      </span>
    </span>
  ) : null

  const onSubmit = async (data: any) => {
    const { quantity } = data

    await handleAddToCart(quantity)
    setOpen(false)

    setQuickViewSuccessNumber(quantity)
    setQuickViewSuccessOpen(true)

    setTimeout(() => {
      setQuickViewSuccessOpen(false)
    }, 3000)
  }

  const shortDescription = productDetails.shortDescription ? (
    <RichContent html={productDetails.shortDescription.html} />
  ) : null

  const onError = (errors: any) => {
    // 找到第一个错误字段
    const firstError = Object.keys(errors)[0]

    const errorElement = document.querySelector(`#form-error`)

    if (errorElement) {
      errorElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      })
    }
  }

  return (
    <div className="mb-[20px] lg_mb-0">
      <div
        className={cn(
          "mx-auto",
          "flex h-full flex-col gap-[10px] lg_flex-row lg_gap-x-[40px]"
        )}
      >
        <div className="mb-[10px] block lg_hidden">
          <h1
            aria-live="polite"
            className="mb-[10px] font-poppins text-[18px] font-[600] leading-[1.4] text-title"
            data-cy="ProductFullDetail-productName"
          >
            {formatString(productDetails?.name ?? "")}
          </h1>
          {rating}
        </div>
        <section className="w-full lg_w-[626px]">
          {isMobile ? (
            <ProductCarousel
              images={mediaGalleryEntries}
              videoUrl={productDetails?.videoUrl ?? ""}
              seoColorLabel={seoColorLabel}
              name={productDetails.name ?? ""}
            />
          ) : (
            <ProductImageCarousel
              gallery={mediaGalleryEntries}
              videoUrl={productDetails?.videoUrl ?? ""}
              productName={productDetails.name ?? ""}
            />
          )}
        </section>

        <div className="overflow-y-auto lg_max-h-[675px] lg_w-[430px] lg_pr-[20px]">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit, onError)}
              className="w-full"
            >
              <div className="">
                <section className={cn("flex flex-col", "px-0")}>
                  <div className="hidden items-start gap-x-2.5 lg_flex">
                    <h1
                      aria-live="polite"
                      className="mb-[20px] font-poppins text-[20px] font-[600] leading-[1.4] text-title"
                      data-cy="ProductFullDetail-productName"
                    >
                      {formatString(productDetails?.name ?? "")}
                    </h1>
                    <button
                      type="button"
                      className="flex-none lg_hidden"
                      onClick={() => {
                        setShowShareLinkModal(true)
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="25"
                        height="25"
                        fill="none"
                        viewBox="0 0 25 25"
                      >
                        <path
                          fill="#2F6132"
                          d="M20.955 14.375a.875.875 0 0 0-.875.875v2.025a3.18 3.18 0 0 1-3.176 3.176H8.103a3.18 3.18 0 0 1-3.175-3.176V7.632a3.18 3.18 0 0 1 3.175-3.176h1.6a.875.875 0 0 0 0-1.75h-1.6a4.93 4.93 0 0 0-4.925 4.926v9.64A4.93 4.93 0 0 0 8.103 22.2h8.801a4.93 4.93 0 0 0 4.926-4.927V15.25a.875.875 0 0 0-.875-.875"
                        />
                        <path
                          fill="#2F6132"
                          d="M13.28 12.389a.88.88 0 0 0 .625-.266l5.92-6.125V8.84a.874.874 0 1 0 1.75 0V4.966a2.136 2.136 0 0 0-2.133-2.133h-3.58a.875.875 0 1 0 0 1.75h2.902l-6.112 6.325a.875.875 0 0 0 .625 1.482z"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="hidden items-center justify-between lg_flex lg_flex-col lg_items-stretch lg_justify-start">
                    <div className="mb-[20px] flex items-center">
                      {rating}
                      {questions}
                    </div>

                    <div className="font-poppins text-[24px] font-[600] leading-[1.4] text-title">
                      <Price
                        currencyCode={productDetails.price.currency}
                        value={productDetails.price.value}
                      />
                      <span className="font-normal text-paragraph line-through ">
                        {product?.price_range?.maximum_price?.discount
                          ?.amount_off &&
                        product?.price_range?.maximum_price?.discount
                          ?.amount_off > 0 ? (
                          <Price
                            currencyCode={
                              product?.price?.regularPrice?.amount?.currency
                            }
                            value={product?.price?.regularPrice?.amount?.value}
                          />
                        ) : null}
                      </span>
                    </div>
                  </div>

                  <div className="mt-[20px] flex items-center justify-between lg_hidden">
                    <div className="font-poppins text-[24px] font-[600] leading-[1.4] text-title">
                      <Price
                        currencyCode={productDetails.price.currency}
                        value={productDetails.price.value}
                      />
                      <span className="font-normal text-paragraph line-through ">
                        {product?.price_range?.maximum_price?.discount
                          ?.amount_off &&
                        product?.price_range?.maximum_price?.discount
                          ?.amount_off > 0 ? (
                          <Price
                            currencyCode={
                              product?.price?.regularPrice?.amount?.currency
                            }
                            value={product?.price?.regularPrice?.amount?.value}
                          />
                        ) : null}
                      </span>
                    </div>
                    <div
                      className={classes.shareIcon}
                      onClick={() => {
                        setShowShareLinkModal(true)
                      }}
                    ></div>
                  </div>

                  {shortDescription}
                </section>

                <FormError errors={errors.get("form") || []} />
                <section className={"pt-[10px] lg_pt-[40px]"}>
                  <StandardOptions
                    configurableOptions={product.configurable_options}
                    variants={product.variants}
                    product={product}
                    optionSpecs={optionSpecs}
                    handleClick={handleClick}
                    hasSizeChart={
                      !!product.sku && SIZE_CHART_SKU.includes(product.sku)
                    }
                    onShowSizeChart={() => {
                      if (product.sku) {
                        mittInstance.emit("showSizeChart", {
                          sku: product.sku!,
                        })
                      }
                    }}
                  />
                </section>

                {isOutOfStock ? (
                  <div className="mt-[40px] flex items-center font-poppins font-[600] text-[#DBA802]">
                    <span className={classes.warningIcon}></span>
                    The item is currently out of stock
                  </div>
                ) : isPreOrder ? (
                  <>{note}</>
                ) : (
                  <div className="mt-[40px] flex items-center">
                    <span className={classes.shippingIcon}></span>
                    <span className="ml-[10px] font-sans text-sm leading-[1.4] text-title lg_text-[16px]">
                      Get it by:{" "}
                      <span className="font-poppins font-[600] text-primary">
                        {calculateDeliveryDate(2, 5)}
                      </span>
                    </span>
                  </div>
                )}

                <div className="mt-[20px] flex items-center justify-between gap-[10px]">
                  {isOutOfStock ? null : (
                    <div className="w-[100px]">
                      <QuantitySelect showQuantity={false} form={form} />
                    </div>
                  )}
                  <div className="flex-1">
                    <AddCartButton
                      isPreOrder={isPreOrder}
                      isAddingProduct={isAddingProduct}
                      addToCartStatus={addToCartStatus}
                      label={label}
                      isOutOfStock={isOutOfStock}
                      setNotifyMeFormDialogIsOpen={setNotifyMeFormDialogIsOpen}
                      note={note}
                      isSupportedProductType={isSupportedProductType}
                    />
                  </div>
                </div>

                <div className="mt-[20px] flex items-center gap-[12px] rounded-[10px] bg-[#F7F7F5] px-[5px] py-[10px] lg_gap-[20px] lg_p-[20px]">
                  <span className={classes.daysIcon}></span>
                  <div className="flex flex-col items-start gap-[5px] lg_gap-[10px]">
                    <p className="lg_text-normal font-poppins text-sm font-[600] leading-[1.2] text-title">
                      Free Shipping & 30-Day Free Returns
                    </p>
                    <p className="font-sans text-sm text-title">
                      Shop with confidence:free shipping and risk-free 30-day
                      returns.{" "}
                    </p>
                  </div>
                </div>
                <div className="mt-[40px]">
                  <ProductDetailAndShipping
                    productDetails={productDetails}
                    product={product}
                  />
                </div>

                <Link
                  href={`/${product.url_key}.html`}
                  className="mt-[20px] inline-block font-sans text-[18px] font-[600] uppercase text-title underline"
                >
                  View Full Product Details
                </Link>
              </div>
            </form>
          </Form>
        </div>
      </div>

      <NewNotifyMeFormDialog
        isOpen={notifyMeFormDialogIsOpen}
        setNotifyMeFormDialogIsOpen={setNotifyMeFormDialogIsOpen}
        product={product}
        variants={product.variants}
        configurable_options={product.configurable_options}
        optionSelections={optionSelections}
      />
    </div>
  )
}
